/**
 * Header component
 * Top navigation bar with theme toggle and user menu
 */

import { Layout, Space, Badge, Button, Dropdown, Typography, Empty } from 'antd';
import type { MenuProps } from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Switch, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import { useSiteAuth } from '@/stores/useSiteAuthStore';
import { useNotificationStore, useNotificationHistory, type NotificationType } from '@/stores/useNotificationStore';
import { formatRelativeTime } from '@/utils/formatters';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

/**
 * Get icon for notification type
 */
function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'success':
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    case 'error':
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    case 'warning':
      return <WarningOutlined style={{ color: '#faad14' }} />;
    case 'info':
    default:
      return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
  }
}

export function Header() {
  const { theme, toggleTheme } = usePreferencesStore();
  const { isRealAdmin, viewAsWorker, toggleViewAsWorker } = useSiteAuth();
  const { unreadCount } = useNotificationStore();
  const { history, markAsRead, markAllAsRead, clearHistory } = useNotificationHistory();
  const isDarkMode = theme === 'dark';

  // Build notification dropdown menu
  const notificationMenuItems: MenuProps['items'] = history.length === 0
    ? [
        {
          key: 'empty',
          disabled: true,
          label: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No notifications"
              style={{ margin: '16px 0' }}
            />
          ),
        },
      ]
    : [
        ...history.slice(0, 10).map((item) => ({
          key: item.id,
          label: (
            <div
              style={{
                padding: '4px 0',
                opacity: item.read ? 0.6 : 1,
                maxWidth: 280,
              }}
              onClick={() => markAsRead(item.id)}
            >
              <Space align="start">
                {getNotificationIcon(item.type)}
                <div>
                  <Text strong={!item.read} style={{ display: 'block' }}>
                    {item.title}
                  </Text>
                  {item.message && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.message}
                    </Text>
                  )}
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                    {formatRelativeTime(item.createdAt.toString())}
                  </Text>
                </div>
              </Space>
            </div>
          ),
        })),
        { type: 'divider' as const },
        {
          key: 'actions',
          label: (
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button
                type="text"
                size="small"
                icon={<CheckOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  markAllAsRead();
                }}
              >
                Mark all read
              </Button>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  clearHistory();
                }}
              >
                Clear
              </Button>
            </Space>
          ),
        },
      ];

  return (
    <AntHeader
      style={{
        padding: '0 24px',
        background: isDarkMode ? '#141414' : '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${isDarkMode ? '#303030' : '#f0f0f0'}`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        height: 64,
      }}
    >
      {/* Left side spacer */}
      <div />

      {/* Right side */}
      <Space size={16}>
        {/* Notifications */}
        <Dropdown
          menu={{ items: notificationMenuItems }}
          trigger={['click']}
          placement="bottomRight"
          overlayStyle={{ minWidth: 320 }}
        >
          <Badge count={unreadCount} size="small" offset={[-2, 2]}>
            <Button
              type="text"
              icon={<BellOutlined />}
              aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
            />
          </Badge>
        </Dropdown>

        {/* Theme toggle */}
        <ThemeToggle isDark={isDarkMode} onToggle={toggleTheme} />

        {/* View as Worker toggle - admin only */}
        {isRealAdmin && (
          <Tooltip title={viewAsWorker ? 'Viewing as Worker — click to switch back to Admin' : 'View as Worker'}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <EyeOutlined style={{ color: viewAsWorker ? '#faad14' : undefined, fontSize: 14 }} />
              <Switch
                checked={viewAsWorker}
                onChange={toggleViewAsWorker}
                size="small"
                style={viewAsWorker ? { background: '#faad14' } : undefined}
              />
              {viewAsWorker && <Text style={{ fontSize: 11, color: '#faad14' }}>Worker</Text>}
            </div>
          </Tooltip>
        )}

        {/* User menu */}
        <UserMenu />
      </Space>
    </AntHeader>
  );
}

export default Header;
